import type React from "react";
import type { ReactNode } from "react";

import { faBooks, faPersonWalking, faSquareList } from "@fortawesome/pro-regular-svg-icons";
import {
  faBooks as faBooksSolid,
  faPersonWalking as faPersonWalkingSolid,
  faSquareList as faSquareListSolid,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import invariant from "ts-invariant";

import { useProfileContext } from "contexts/ProfileContext";
import { BackButton } from "shared/atoms/BackButton";
import { BaseButton } from "shared/atoms/BaseButton";
import { ButtonsGroup } from "shared/atoms/ButtonsGroup";
import { View } from "shared/containers/View";
import { type Breadcrumb, createBreadCrumb } from "shared/containers/View/components/ViewBreadcrumbs";
import { useIsInRoute } from "utils/hooks";

export type Category = "exercises" | "lessons" | "templates";
type TabItem = {
  value: Category;
  label: string;
  icon: ReactNode;
  iconActive: ReactNode;
  link: string;
};

const MODIFY_PATHS = ["confirmation", "edit", "new"];

export const translateModifyPath = (path: string): string => {
  const keyMap: Record<string, string> = {
    confirmation: i18n.t("common.confirmation"),
    edit: i18n.t("common.edit"),
    new: i18n.t("common.new")[0],
  };

  return keyMap[path] || "";
};

const getSingularLabel = (category: Category): string => {
  const keyMap: Record<Category, string> = {
    exercises: i18n.t("library.exercise"),
    lessons: i18n.t("library.lesson"),
    templates: i18n.t("common.template"),
  };

  return keyMap[category] || "";
};

const createAdditionalBreadcrumbs = (
  tabBase: string,
  activeTab?: TabItem | null,
  modifyPath?: string,
  id?: string
): Breadcrumb[] => {
  const additionalBreadcrumbs: Breadcrumb[] = [];
  if (activeTab) {
    additionalBreadcrumbs.push(createBreadCrumb(activeTab.label, `/library/${tabBase}/${activeTab.value}`));
  }
  if (id && activeTab) {
    additionalBreadcrumbs.push(
      createBreadCrumb(`${getSingularLabel(activeTab.value)} ${id}`, `/library/${tabBase}/${activeTab.value}/${id}`)
    );
  }
  if (modifyPath) {
    additionalBreadcrumbs.push(createBreadCrumb(translateModifyPath(modifyPath), ""));
  }

  return additionalBreadcrumbs;
};

export const Library: React.FC = () => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const isModifying = useIsInRoute(MODIFY_PATHS);
  const isModifyingTemplate = useIsInRoute(["new_template", "edit_template"]);
  const tabBase = "your-library";

  invariant(profile, "Profile is required");

  const isTabActive = (tabKey: Category) => location.pathname.includes(`/${tabKey}`);
  const TABS: TabItem[] = [
    {
      value: "exercises",
      label: t("common.exercises"),
      icon: <FontAwesomeIcon icon={faPersonWalking} />,
      iconActive: <FontAwesomeIcon icon={faPersonWalkingSolid} />,
      link: `/library/${tabBase}/exercises`,
    },
    {
      value: "lessons",
      label: t("common.lessons"),
      icon: <FontAwesomeIcon icon={faBooks} />,
      iconActive: <FontAwesomeIcon icon={faBooksSolid} />,
      link: `/library/${tabBase}/lessons`,
    },
    {
      value: "templates",
      label: t("common.templates"),
      icon: <FontAwesomeIcon icon={faSquareList} />,
      iconActive: <FontAwesomeIcon icon={faSquareListSolid} />,
      link: `/library/${tabBase}/templates`,
    },
  ];
  const activeTab = TABS.find(tabItem => isTabActive(tabItem.value)) || null;

  const pathSegments = location.pathname.split("/");
  const currentModifyPath = MODIFY_PATHS.find(path => pathSegments.includes(path));

  const additionalBreadcrumbs = createAdditionalBreadcrumbs(tabBase, activeTab, currentModifyPath, params.id);

  const handleCreateNewClick = () => {
    if (activeTab?.value === "templates") {
      navigate(`${tabBase}/templates/new_template`);
      return;
    }

    navigate(activeTab?.value ? `${tabBase}/${activeTab.value}/new` : `${tabBase}/new`);
  };

  return (
    <View
      title={t("library.title")}
      headerContent={!isModifying ? <ButtonsGroup group={TABS} active={activeTab?.value} /> : undefined}
      hideHeader={isModifyingTemplate}
      removePadding={isModifyingTemplate}
      actionButtons={[
        <BaseButton
          text={t("buttons.create_new")}
          icon="plus"
          style={{ flexGrow: 0 }}
          onClick={handleCreateNewClick}
          uppercase={false}
          size="large"
        />,
      ]}
      additionalBreadcrumbs={additionalBreadcrumbs}
    >
      <Box>
        {(params.id || isModifying) && <BackButton />}

        <Outlet />
      </Box>
    </View>
  );
};
